import React, { useState, useRef, useEffect, type ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { copyToClipboard } from '../../../services/clipboard';
import type { CancelReasonOption } from '../../../services/types';
import { cancelReasonKey } from '../../../services/cancelReasons';

// ─── Tokens (dark theme) ─────────────────────────────────────────────────────
export const PINCO_RED = '#FF0123';
export const PINCO_YELLOW = '#E9C05F';
export const PINCO_BG = '#0d0d0d';
export const PINCO_CARD_BG = '#1d1d1d';
export const PINCO_CARD_BG_SOFT = '#262626';
export const PINCO_TEXT = '#ffffff';
export const PINCO_TEXT_MUTED = '#8a8a8a';

// Backward-compatible aliases used across Pinco pages.
// Kept as-is by name to avoid touching every page file; mapped to the new palette.
export const PINCO_GREEN = PINCO_RED;
export const PINCO_GREEN_LIGHT = PINCO_CARD_BG_SOFT;
export const PINCO_YELLOW_LIGHT = PINCO_CARD_BG_SOFT;
export const PINCO_RED_LIGHT = PINCO_CARD_BG_SOFT;
export const PINCO_GREY = PINCO_TEXT_MUTED;
export const PINCO_BORDER = '#2a2a2a';

// ─── Root wrapper — full viewport, scrollable when content overflows ──────────
export const PincoRoot = styled.div`
  --btn-font-family: ;
  min-height: 100dvh;
  background: ${PINCO_BG};
  color: ${PINCO_TEXT};
  font-family: 'PT Mono', monospace, sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Centred container — margin: auto top/bottom pushes it to vertical centre
// when parent is taller than card; scrolls normally when card is taller
export const PincoCard = styled.div`
  width: 100%;
  max-width: 550px;
  margin: auto 0;
  padding: 24px 12px 40px;
  box-sizing: border-box;
  text-align: center;
  font-weight: 400;
  line-height: 1.5;
`;

// ─── SVG Flags (emoji flags don't render on Windows) ─────────────────────────
const FLAG_STYLE = { borderRadius: 3, display: 'block', flexShrink: 0 } as const;
const f = (children: React.ReactNode) => (
  <svg width="22" height="15" viewBox="0 0 22 15" style={FLAG_STYLE}>
    {children}
  </svg>
);

const FlagRU = () => f(<><rect width="22" height="5" y="0" fill="#fff"/><rect width="22" height="5" y="5" fill="#0039A6"/><rect width="22" height="5" y="10" fill="#D52B1E"/></>);
const FlagEN = () => (
  <svg width="22" height="15" viewBox="0 0 44 30" style={FLAG_STYLE}>
    <rect width="44" height="30" fill="#012169"/>
    <path d="M0,0 L44,30 M44,0 L0,30" stroke="#fff" strokeWidth="6"/>
    <path d="M0,0 L44,30 M44,0 L0,30" stroke="#C8102E" strokeWidth="3.5"/>
    <path d="M0,15 H44" stroke="#fff" strokeWidth="10"/>
    <path d="M22,0 V30" stroke="#fff" strokeWidth="10"/>
    <path d="M0,15 H44" stroke="#C8102E" strokeWidth="6"/>
    <path d="M22,0 V30" stroke="#C8102E" strokeWidth="6"/>
  </svg>
);
const FlagUK = () => f(<><rect width="22" height="7" y="0" fill="#005BBB"/><rect width="22" height="8" y="7" fill="#FFD500"/></>);
const FlagDE = () => f(<><rect width="22" height="5" y="0" fill="#000"/><rect width="22" height="5" y="5" fill="#D00"/><rect width="22" height="5" y="10" fill="#FFCE00"/></>);
const FlagES = () => f(<><rect width="22" height="4" y="0" fill="#AA151B"/><rect width="22" height="7" y="4" fill="#F1BF00"/><rect width="22" height="4" y="11" fill="#AA151B"/></>);
const FlagAZ = () => f(<><rect width="22" height="5" y="0" fill="#0092BC"/><rect width="22" height="5" y="5" fill="#E8112D"/><rect width="22" height="5" y="10" fill="#00B140"/></>);
const FlagTR = () => f(<><rect width="22" height="15" fill="#E30A17"/><circle cx="9" cy="7.5" r="4" fill="#fff"/><circle cx="10.5" cy="7.5" r="3.2" fill="#E30A17"/><polygon points="14,7.5 15.5,6.5 15.5,8.5" fill="#fff"/></>);
const FlagKY = () => f(<><rect width="22" height="15" fill="#E8112D"/><circle cx="11" cy="7.5" r="4" fill="#F9CB39" fillOpacity="0.9"/></>);
const FlagID = () => f(<><rect width="22" height="7" y="0" fill="#CE1126"/><rect width="22" height="8" y="7" fill="#fff"/></>);
const FlagKK = () => f(<><rect width="22" height="15" fill="#00AFCA"/><circle cx="7" cy="7.5" r="3" fill="#FFE600" fillOpacity="0.9"/></>);
const FlagHI = () => f(<><rect width="22" height="5" y="0" fill="#FF9933"/><rect width="22" height="5" y="5" fill="#fff"/><rect width="22" height="5" y="10" fill="#138808"/><circle cx="11" cy="7.5" r="1.5" fill="#000080"/></>);
const FlagPT = () => f(<><rect width="22" height="15" fill="#009C3B"/><rect width="8" height="15" x="0" fill="#009C3B"/><polygon points="8,0 20,7.5 8,15" fill="#FEDF00"/><circle cx="14" cy="7.5" r="2.5" fill="#002776"/></>);
const FlagUZ = () => f(<><rect width="22" height="5" y="0" fill="#1EB53A"/><rect width="22" height="1" y="5" fill="#fff"/><rect width="22" height="3" y="6" fill="#fff"/><rect width="22" height="1" y="9" fill="#fff"/><rect width="22" height="5" y="10" fill="#CE1126"/></>);

// ─── Language Dropdown ────────────────────────────────────────────────────────
const LANGS = [
  { code: 'ru', Flag: FlagRU, label: 'Русский' },
  { code: 'en', Flag: FlagEN, label: 'English' },
  { code: 'uk', Flag: FlagUK, label: 'Українська' },
  { code: 'de', Flag: FlagDE, label: 'Deutsch' },
  { code: 'es', Flag: FlagES, label: 'Español' },
  { code: 'az', Flag: FlagAZ, label: 'Azərbaycan' },
  { code: 'tr', Flag: FlagTR, label: 'Türkçe' },
  { code: 'ky', Flag: FlagKY, label: 'Кыргызча' },
  { code: 'id', Flag: FlagID, label: 'Indonesia' },
  { code: 'kk', Flag: FlagKK, label: 'Қазақша' },
  { code: 'hi', Flag: FlagHI, label: 'हिन्दी' },
  { code: 'pt', Flag: FlagPT, label: 'Português' },
  { code: 'uz', Flag: FlagUZ, label: 'Oʻzbekcha' },
];

const LangWrapper = styled.div`
  position: relative;
  height: 40px;
  width: 70px;
`;

const LangTrigger = styled.button`
  display: flex;
  align-items: center;
  border: 1px solid ${PINCO_BORDER};
  background-color: ${PINCO_CARD_BG};
  color: ${PINCO_TEXT};
  width: 100%;
  height: 100%;
  padding: 0 4px;
  font-size: .875rem;
  border-radius: 0.25rem;
  cursor: pointer;
  box-sizing: border-box;
  &::after {
    display: inline-block;
    flex-shrink: 0;
    margin-left: .255em;
    vertical-align: .255em;
    content: "";
    border-top: .3em solid;
    border-right: .3em solid transparent;
    border-bottom: 0;
    border-left: .3em solid transparent;
  }
  &:hover { background-color: ${PINCO_CARD_BG_SOFT}; }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: ${PINCO_CARD_BG};
  border: 1px solid ${PINCO_BORDER};
  border-radius: 18px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  overflow: hidden;
  min-width: 130px;
  z-index: 100;
`;

const LangOption = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: ${({ $active }) => ($active ? PINCO_CARD_BG_SOFT : 'transparent')};
  border: none;
  cursor: pointer;
  font-family: 'PT Mono', monospace;
  font-size: 13px;
  color: ${({ $active }) => ($active ? PINCO_RED : PINCO_TEXT)};
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  text-align: left;
  transition: background 0.1s;
  &:hover { background: ${PINCO_CARD_BG_SOFT}; }
`;

const LangFlag = styled.span`
  font-size: 18px;
  line-height: 1;
`;

const LangFlagImg = styled.span`
  display: flex;
  & svg {
    width: 40px;
    height: auto;
    border-radius: 4px;
    margin-right: 5px;
    box-shadow: 0 2px 10px #0000004d;
  }
`;

const LangSelector = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = LANGS.find(l => l.code === i18n.language) ?? LANGS[0];

  return (
    <LangWrapper ref={ref}>
      <LangTrigger onClick={() => setOpen(v => !v)}>
        <LangFlagImg><current.Flag /></LangFlagImg>
      </LangTrigger>
      {open && (
        <Dropdown>
          {LANGS.map(({ code, Flag, label }) => (
            <LangOption
              key={code}
              $active={code === i18n.language}
              onClick={() => { i18n.changeLanguage(code); setOpen(false); }}
            >
              <LangFlag><Flag /></LangFlag>
              {label}
            </LangOption>
          ))}
        </Dropdown>
      )}
    </LangWrapper>
  );
};

// ─── Header ──────────────────────────────────────────────────────────────────
const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  padding-left: 16px;
  padding-right: 16px;
  margin-top: 24px;
  margin-bottom: 16px;
  gap: 12px;
`;

const LogoCell = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const LangCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Title = styled.h1`
  margin: 0;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: ${PINCO_TEXT};
  font-family: 'PT Mono', monospace, sans-serif;
  font-size: 22px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
`;

const PincoLogo = () => (
  <svg width="80" height="23" viewBox="0 0 80 23" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#pinco-logo-clip)">
      <path d="M4.67809 22.6921H0V2.42188H8.21662C8.97051 2.42188 9.69575 2.51865 10.3924 2.71149C11.0889 2.90468 11.7401 3.17794 12.3462 3.53124C12.952 3.88455 13.5057 4.30853 14.0067 4.8035C14.5076 5.29814 14.937 5.84741 15.2951 6.45031C15.6529 7.05356 15.9295 7.6989 16.1255 8.38673C16.3211 9.07456 16.419 9.79093 16.419 10.5355C16.419 11.6476 16.2041 12.6961 15.7747 13.6805C15.3454 14.6652 14.7584 15.525 14.0141 16.2601C13.2696 16.9952 12.3987 17.5749 11.4016 17.9987C10.4042 18.4228 9.34256 18.6347 8.21662 18.6347H4.67809V22.6921ZM4.63408 7.05702V14.007H8.20334C8.69243 14.007 9.15253 13.9161 9.58397 13.7344C10.0151 13.5527 10.3924 13.3045 10.7158 12.9899C11.0393 12.6752 11.294 12.3059 11.4806 11.8819C11.6671 11.4583 11.7604 11.0082 11.7604 10.532C11.7604 10.0558 11.6671 9.60574 11.4806 9.18211C11.294 8.75849 11.039 8.38916 10.7158 8.07414C10.3927 7.75913 10.0151 7.51129 9.58397 7.32959C9.15288 7.14789 8.69243 7.05702 8.20334 7.05702H4.63408Z" fill={PINCO_RED}/>
      <path d="M22.4785 22.6762H17.8633V2.40625H22.4785V22.6765V22.6762Z" fill={PINCO_RED}/>
      <path d="M36.9563 22.6921L29.9491 10.7861V22.6921H25.3398V2.42188H30.1004L37.0828 14.2844V2.42188H41.7132V22.6921H36.9563Z" fill={PINCO_RED}/>
      <path d="M56.436 17.6729C55.6257 18.1008 54.7015 18.3433 53.7206 18.3433C50.5114 18.3433 47.91 15.7512 47.91 12.5535C47.91 9.35604 50.5114 6.76382 53.7206 6.76382C54.702 6.76382 55.6263 7.00679 56.4371 7.43457C57.1051 6.02344 58.0403 4.76266 59.1782 3.717C57.5949 2.74722 55.7315 2.1875 53.7368 2.1875C47.9902 2.1875 43.332 6.82891 43.332 12.5547C43.332 18.2804 47.9902 22.9218 53.7368 22.9218C55.7315 22.9218 57.5949 22.3619 59.1782 21.3923C58.0396 20.3462 57.1041 19.0848 56.436 17.6725V17.6729Z" fill={PINCO_RED}/>
      <path d="M67.3616 2.17188C61.6151 2.17188 56.957 6.8133 56.957 12.539C56.957 18.2646 61.6151 22.9061 67.3616 22.9061C73.1079 22.9061 77.7661 18.2646 77.7661 12.539C77.7661 6.8133 73.1079 2.17188 67.3616 2.17188ZM67.341 18.3287C64.1319 18.3287 61.5302 15.7366 61.5302 12.539C61.5302 9.34145 64.1319 6.74925 67.341 6.74925C70.55 6.74925 73.1516 9.34145 73.1516 12.539C73.1516 15.7366 70.55 18.3287 67.341 18.3287Z" fill={PINCO_RED}/>
      <path d="M67.3361 15.3011C68.8376 15.3011 70.0549 14.0882 70.0549 12.592C70.0549 11.0957 68.8376 9.88281 67.3361 9.88281C65.8344 9.88281 64.6172 11.0957 64.6172 12.592C64.6172 14.0882 65.8344 15.3011 67.3361 15.3011Z" fill={PINCO_RED}/>
      <path d="M79.9113 3.61092C79.7919 3.3227 79.5092 3.13509 79.1966 3.13509H76.8241V0.771215C76.8241 0.459675 76.6362 0.178072 76.3468 0.059025C76.0574 -0.0614138 75.7247 0.00646362 75.5034 0.226109L72.4089 3.36099C72.264 3.50545 72.1822 3.70108 72.1822 3.9061V6.67028L66.9418 11.8919C66.7824 12.0333 66.6836 12.2401 66.6836 12.4691C66.6836 12.8947 67.0302 13.2401 67.4573 13.2401C67.6936 13.2401 67.8732 13.1214 68.0482 12.9696L73.2761 7.76048H76.0501C76.2555 7.76048 76.4521 7.67904 76.5972 7.53459L79.7432 4.45121C79.9647 4.23051 80.0312 3.89949 79.911 3.61092H79.9113Z" fill={PINCO_YELLOW}/>
    </g>
    <defs>
      <clipPath id="pinco-logo-clip"><rect width="80" height="23" fill="white"/></clipPath>
    </defs>
  </svg>
);

export const PincoHeader = () => {
  const { t } = useTranslation();
  return (
    <HeaderRow>
      <LogoCell><PincoLogo /></LogoCell>
      <Title>{t('header.title')}</Title>
      <LangCell><LangSelector /></LangCell>
    </HeaderRow>
  );
};

// ─── Step Indicator ──────────────────────────────────────────────────────────
type StepStatus = 'done' | 'active' | 'pending';

const StepsRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-evenly;
  gap: 16px;
  margin-bottom: 24px;
  margin-top: 8px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
`;

const StepInner = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 60px;
  align-items: center;
`;

const StepCircle = styled.div<{ $status: StepStatus }>`
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-weight: 600;
  font-family: 'PT Mono', monospace, sans-serif;
  transition: all 0.3s ease;
  background-color: ${PINCO_TEXT_MUTED};
  color: ${PINCO_BG};
  ${({ $status }) =>
    $status === 'pending' && `background-color: ${PINCO_BG}; border: 1px solid ${PINCO_TEXT_MUTED}; color: ${PINCO_TEXT_MUTED};`}
  ${({ $status }) =>
    $status === 'done' && `background-color: ${PINCO_BG}; border: 1px solid ${PINCO_TEXT_MUTED}; color: ${PINCO_TEXT_MUTED};`}
`;

const StepLabel = styled.div<{ $status: StepStatus }>`
  margin: 8px 0 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  font-size: 11px;
  text-align: center;
  max-width: 80px;
  color: ${PINCO_TEXT_MUTED};
`;

const StepConnector = styled.div`
  margin: 16px 0 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: ${PINCO_TEXT_MUTED};
  font-size: 9px;
  max-height: 10px;
  overflow: hidden;
  flex-shrink: 0;
`;

export const CheckIcon = ({ width = 18, height = 13, stroke = PINCO_TEXT }: { width?: number; height?: number; stroke?: string }) => (
  <svg width={width} height={height} viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 6.528L6.528 11.056L15.6 2" stroke={stroke} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * Шаги — это обещание: клиент по ним понимает, что его ждёт дальше. Чек требуется не всегда, и
 * там, где его не будет, шаг про чек обещает работу, которой не случится. Нумерация идёт по
 * отрисованным шагам, а не по их именам, иначе после «1» шёл бы «3».
 */
export const PincoSteps = ({ step, withReceipt = true }: { step: 1 | 2 | 3; withReceipt?: boolean }) => {
  const { t } = useTranslation();
  const getStatus = (n: number): StepStatus => {
    if (n < step) return 'done';
    if (n === step) return 'active';
    return 'pending';
  };
  const steps = [
    { n: 1, label: t('steps.payByRequisites') },
    ...(withReceipt ? [{ n: 2, label: t('steps.uploadReceipt') }] : []),
    { n: 3, label: t('steps.processPayment') },
  ];
  return (
    <StepsRow>
      {steps.map((s, idx) => (
        <React.Fragment key={s.n}>
          <StepItem>
            <StepInner>
              <StepCircle $status={getStatus(s.n)}>
                {getStatus(s.n) === 'done' ? <CheckIcon /> : idx + 1}
              </StepCircle>
              <StepLabel $status={getStatus(s.n)}>{s.label}</StepLabel>
            </StepInner>
          </StepItem>
          {idx < steps.length - 1 && (
            <StepConnector>- - - - - - -</StepConnector>
          )}
        </React.Fragment>
      ))}
    </StepsRow>
  );
};

// ─── Bottom info ──────────────────────────────────────────────────────────────
const BottomArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TimerPill = styled.div`
  margin: 50px 0 20px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  display: inline-block;
  font-size: 15px;
  color: ${PINCO_TEXT};
  background-color: ${PINCO_CARD_BG_SOFT};
  width: fit-content;
  text-align: center;
  padding: 10px 25px;
  border-radius: 28px;
  font-family: 'PT Mono', monospace, sans-serif;
`;

const TimerValue = styled.span`
  color: ${PINCO_TEXT};
  font-family: PT Sans, sans-serif;
`;

const OrderNumber = styled.div`
  font-size: 14px;
  color: ${PINCO_TEXT_MUTED};
  font-family: PT Sans, sans-serif;
  text-align: center;
  margin-bottom: 25px;
`;

export const PincoBottom = ({ timeLeft, uuid }: { timeLeft: string; uuid?: string }) => {
  const { t } = useTranslation();
  return (
    <BottomArea>
      <TimerPill>
        {t('common.awaitingTransfer')} <TimerValue>{timeLeft}</TimerValue>
      </TimerPill>
      {uuid && <OrderNumber>{t('common.orderNumber')} {uuid}</OrderNumber>}
    </BottomArea>
  );
};

// ─── Shared icons ─────────────────────────────────────────────────────────────
export const ClipIcon = ({ size = 20, color = '#fff' }: { size?: number; color?: string }) => (
  <svg height={size} width={size} viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path fill={color} d="M454.821,253.582L273.256,435.14c-11.697,11.697-25.124,20.411-39.484,26.235c-21.529,8.729-45.165,10.928-67.755,6.55c-22.597-4.378-44.054-15.25-61.597-32.784c-11.69-11.69-20.396-25.118-26.227-39.484c-8.729-21.529-10.929-45.165-6.55-67.748c4.386-22.597,15.25-44.055,32.778-61.596l203.13-203.13c7.141-7.134,15.299-12.43,24.035-15.969c13.1-5.318,27.516-6.656,41.263-3.994c13.769,2.677,26.798,9.27,37.498,19.963c7.133,7.134,12.423,15.292,15.968,24.035c5.318,13.092,6.657,27.502,3.987,41.264c-2.67,13.762-9.262,26.783-19.955,37.498L213.261,363.064c-2.534,2.528-5.375,4.364-8.436,5.61c-4.571,1.851-9.661,2.335-14.495,1.396c-4.848-0.954-9.355-3.225-13.15-7.006c-2.534-2.534-4.364-5.368-5.603-8.429c-1.865-4.571-2.342-9.668-1.402-14.495c0.947-4.841,3.225-9.355,7.005-13.149l175.521-175.528l-29.616-29.617l-175.528,175.52c-6.536,6.536-11.505,14.182-14.801,22.313c-4.941,12.195-6.166,25.473-3.702,38.202c2.449,12.73,8.686,24.989,18.503,34.799c6.543,6.55,14.182,11.519,22.305,14.809c12.202,4.948,25.473,6.165,38.21,3.702c12.722-2.449,24.989-8.678,34.806-18.511L439.97,195.602c11.142-11.149,19.571-24.113,25.167-37.917c8.394-20.717,10.48-43.314,6.294-64.971c-4.179-21.643-14.73-42.432-31.46-59.155c-11.149-11.142-24.114-19.571-37.918-25.166c-20.717-8.401-43.314-10.48-64.971-6.301c-21.643,4.186-42.431,14.737-59.155,31.468L74.803,236.695c-15.713,15.691-27.552,33.931-35.426,53.352c-11.817,29.154-14.765,60.97-8.863,91.462c5.888,30.478,20.717,59.696,44.29,83.254c15.698,15.713,33.931,27.552,53.36,35.426c29.146,11.811,60.97,14.758,91.455,8.863c30.478-5.895,59.696-20.717,83.254-44.29l181.566-181.564L454.821,253.582z"/>
  </svg>
);

// ─── Upload button (single shared component with MUI ripple) ─────────────────
const muiRippleIn = keyframes`
  from { transform: translate(-50%, -50%) scale(0); opacity: 0.35; }
  to   { transform: translate(-50%, -50%) scale(1); opacity: 0.35; }
`;
const muiRippleOut = keyframes`
  from { opacity: 0.35; }
  to   { opacity: 0; }
`;

const UploadBtnEl = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  outline: 0;
  margin: 0 0 16px;
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
  text-decoration: none;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  min-width: 64px;
  padding: 6px 16px;
  color: ${PINCO_TEXT};
  width: 100%;
  text-transform: none;
  background-color: transparent;
  font-size: 15px;
  box-shadow: none;
  transition: opacity 0.2s, background-color 0.2s;
  height: 46px;
  border-radius: 10px;
  border: 1.5px solid ${PINCO_BORDER};
  max-width: 750px;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  &:hover { background-color: ${PINCO_CARD_BG_SOFT}; }
`;

const UploadRippleEl = styled.span<{ $x: number; $y: number; $size: number; $out: boolean }>`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  width:  ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  left: ${({ $x }) => $x}px;
  top:  ${({ $y }) => $y}px;
  background: rgba(255, 255, 255, 0.45);
  animation: ${({ $out }) => ($out ? muiRippleOut : muiRippleIn)}
             ${({ $out }) => ($out ? '0.4s' : '0.5s')} ease forwards;
`;

const UploadBtnIconWrap = styled.span`
  display: inherit;
  margin-right: 8px;
  margin-left: -4px;
  font-size: 20px;
`;

const UploadBtnText = styled.span`
  margin: 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 1rem;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: ${PINCO_TEXT};
  font-weight: 600;
`;

interface UploadRipple { id: number; x: number; y: number; size: number; out: boolean; }

export const PincoUploadBtn = ({ label, onClick }: { label: string; onClick?: () => void }) => {
  const [ripples, setRipples] = useState<UploadRipple[]>([]);
  const activeId = useRef<number | null>(null);
  const idRef = useRef(0);

  const onDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const size = 2 * Math.max(
      Math.hypot(x, y),
      Math.hypot(rect.width - x, y),
      Math.hypot(x, rect.height - y),
      Math.hypot(rect.width - x, rect.height - y),
    );
    const id = ++idRef.current;
    activeId.current = id;
    setRipples(prev => [...prev, { id, x, y, size, out: false }]);
  };

  const onUp = () => {
    const id = activeId.current;
    if (id === null) return;
    activeId.current = null;
    setRipples(prev => prev.map(r => r.id === id ? { ...r, out: true } : r));
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 450);
  };

  return (
    <UploadBtnEl
      type="button"
      onClick={onClick}
      onMouseDown={onDown}
      onMouseUp={onUp}
      onMouseLeave={onUp}
    >
      {ripples.map(r => (
        <UploadRippleEl key={r.id} $x={r.x} $y={r.y} $size={r.size} $out={r.out} />
      ))}
      <UploadBtnIconWrap><ClipIcon size={25} color={PINCO_TEXT} /></UploadBtnIconWrap>
      <UploadBtnText>{label}</UploadBtnText>
    </UploadBtnEl>
  );
};

// ─── Card variants ────────────────────────────────────────────────────────────
export const GreenCard = styled.div`
  background: ${PINCO_CARD_BG};
  border-radius: 24px;
  padding: 16px;
  margin-bottom: 12px;
`;

export const YellowCard = styled.div`
  background: ${PINCO_CARD_BG};
  border-radius: 24px;
  padding: 16px;
  margin-bottom: 12px;
`;

export const RedCard = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding-top: 56px;
  padding-bottom: 56px;
  background-color: ${PINCO_CARD_BG};
  border-radius: 24px;
  margin-top: 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
`;

export const GreyCard = styled.div`
  background: ${PINCO_CARD_BG};
  border-radius: 24px;
  padding: 16px;
  margin-bottom: 12px;
`;

// ─── Button wrapper ───────────────────────────────────────────────────────────
export const PincoButtonWrap = styled.div`
  margin-bottom: 1rem !important;
  width: 100%;
  & > button {
    margin-bottom: 0;
    font-family: PT Sans, sans-serif;
  }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const btnBase = `
  display: inline-block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  min-height: 38px;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  text-decoration: none;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  margin-bottom: 8px;
  box-sizing: border-box;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
  &:active { opacity: 0.85; }
`;

export const PincoButtonGreen = styled.button`
  ${btnBase}
  background: ${PINCO_TEXT};
  color: #000;
  font-weight: 600;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

export const PincoButtonGrey = styled.button`
  ${btnBase}
  font-family: var(--btn-font-family);
  background: ${PINCO_CARD_BG_SOFT};
  color: ${PINCO_TEXT};
  border: 1px solid ${PINCO_BORDER};
  &:hover { background: ${PINCO_CARD_BG}; }
`;

// ─── Info row ─────────────────────────────────────────────────────────────────
export const InfoRow = styled.div`
  width: 100%;
  padding: 12px 24px;
  text-align: start;
  background-color: ${PINCO_CARD_BG_SOFT};
  color: ${PINCO_TEXT};
  font-size: 14px;
  font-family: 'PT Mono', monospace, sans-serif;
  border-radius: 24px;
  margin-top: 16px;
  margin-bottom: 1rem !important;
  box-sizing: border-box;
`;

// ─── Copy row ─────────────────────────────────────────────────────────────────
const CopyRowWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  background-color: ${PINCO_CARD_BG_SOFT};
  border-radius: 44px;
  padding: 8px 16px;
  margin-bottom: 16px;
  box-sizing: border-box;
`;

const CopyText = styled.div`
  margin: 0 0 0 0;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  color: ${PINCO_TEXT};
  width: 100%;
  font-family: 'PT Mono', monospace, sans-serif;
  font-size: 14px;
  text-align: left;
`;

const CopyLabel = styled.div`
  font-size: 11px;
  color: ${PINCO_TEXT_MUTED};
  font-family: 'PT Mono', monospace;
`;

const CopyBtn = styled.button<{ $copied: boolean }>`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: ${({ $copied }) => ($copied ? 1 : 0.6)};
  transition: opacity 0.2s, transform 0.1s;
  &:active { transform: scale(0.9); }
`;

const CopyIconSvg = () => (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.6654 8.6026V11.4026C10.6654 13.7359 9.73203 14.6693 7.3987 14.6693H4.5987C2.26536 14.6693 1.33203 13.7359 1.33203 11.4026V8.6026C1.33203 6.26927 2.26536 5.33594 4.5987 5.33594H7.3987C9.73203 5.33594 10.6654 6.26927 10.6654 8.6026Z" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.6654 4.6026V7.4026C14.6654 9.73594 13.732 10.6693 11.3987 10.6693H10.6654V8.6026C10.6654 6.26927 9.73203 5.33594 7.3987 5.33594H5.33203V4.6026C5.33203 2.26927 6.26536 1.33594 8.5987 1.33594H11.3987C13.732 1.33594 14.6654 2.26927 14.6654 4.6026Z" stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CopyDoneSvg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 10l4 4 8-8" stroke={PINCO_RED} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const PincoCopyRow = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    copyToClipboard(value).then((ok) => {
      if (!ok) return;
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <CopyRowWrapper>
      <CopyText>
        <CopyLabel>{label}</CopyLabel>
        {value}
      </CopyText>
      <CopyBtn $copied={copied} onClick={handleCopy}>
        {copied ? <CopyDoneSvg /> : <CopyIconSvg />}
      </CopyBtn>
    </CopyRowWrapper>
  );
};

// ─── Success row ──────────────────────────────────────────────────────────────
export const SuccessRow = styled.div`
  background: ${PINCO_GREEN_LIGHT};
  border-radius: 18px;
  padding: 12px 14px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'PT Mono', monospace;
  font-size: 14px;
  color: ${PINCO_GREEN};
`;

// ─── Spinner — SVG arc "run and chase" ───────────────────────────────────────
// r=20 → circumference ≈ 126px
// Two independent animations:
//   svgRotate  — whole SVG spins continuously
//   arcDash    — the arc oscillates between ~15% and ~85% of circumference
//                leading edge stretches ahead, trailing edge catches up
export const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const svgRotate = keyframes`
  100% { transform: rotate(360deg); }
`;

// dashoffset trick:
//  0%   → tiny arc (≈15%)
//  50%  → large arc (≈85%), leading edge already far ahead
//  100% → large arc fully contracted back → same as start + 1 full rotation
const arcDash = keyframes`
  0%   { stroke-dasharray: 1,   126; stroke-dashoffset: 0;    }
  50%  { stroke-dasharray: 107, 126; stroke-dashoffset: -38;  }
  100% { stroke-dasharray: 107, 126; stroke-dashoffset: -126; }
`;

const SpinSvg = styled.svg<{ $size: number }>`
  width:  ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  animation: ${svgRotate} 1.6s linear infinite;
`;

const SpinCircle = styled.circle`
  fill: none;
  stroke: ${PINCO_GREEN};
  stroke-width: 4;
  stroke-linecap: round;
  animation: ${arcDash} 1.4s ease-in-out infinite;
`;

/** Standalone spinner — use where a simple loading indicator is needed */
export const PincoSpinner = ({ size = 24 }: { size?: number }) => (
  <SpinSvg $size={size} viewBox="0 0 50 50">
    <SpinCircle cx="25" cy="25" r="20" />
  </SpinSvg>
);

/** Spinner ring wrapping content (icon/emoji in the center) */
export const PincoSpinnerRing = ({
  size = 48,
  children,
}: {
  size?: number;
  children?: ReactNode;
}) => (
  <div style={{ position: 'relative', width: size, height: size, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <SpinSvg $size={size} viewBox="0 0 50 50" style={{ position: 'absolute', inset: 0 }}>
      <SpinCircle cx="25" cy="25" r="20" />
    </SpinSvg>
    <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
  </div>
);

/** @deprecated use PincoSpinner */
export const Spinner = PincoSpinner;

// ─── PincoScreen — единая обёртка для всех экранов ───────────────────────────
export interface PincoAction {
  label: ReactNode;
  onClick: () => void;
  variant?: 'green' | 'grey';
  confirmLeave?: boolean;
}

interface PincoScreenProps {
  step?: 1 | 2 | 3;
  /** Требуется ли чек: без него шага про загрузку в полосе шагов нет. */
  withReceipt?: boolean;
  uuid?: string;
  timeLeft?: string;   // если передан — показывает таймер (PincoBottom)
  actions?: PincoAction[];
  footerSlot?: ReactNode; // для нестандартных раскладок кнопок
  children: ReactNode;
}

const PincoOrderNum = ({ uuid }: { uuid: string }) => {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: 'center', fontSize: 14, color: PINCO_TEXT_MUTED, fontFamily: 'PT Sans, sans-serif', marginBottom: 25 }}>
      {t('common.orderNumber')} {uuid}
    </div>
  );
};

// ─── Leave-confirm modal ──────────────────────────────────────────────────────
const LCOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const LCBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  max-width: calc(100vw - 32px);
  background-color: ${PINCO_CARD_BG};
  color: ${PINCO_TEXT};
  border-radius: 24px;
  box-shadow:
    rgba(0, 0, 0, 0.2) 0px 11px 15px -7px,
    rgba(0, 0, 0, 0.14) 0px 24px 38px 3px,
    rgba(0, 0, 0, 0.12) 0px 9px 46px 8px;
  padding: 32px;
  white-space: pre-wrap;
  box-sizing: border-box;
`;

const LCText = styled.p`
  margin: 16px 0 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: 0.00938em;
  text-align: left;
`;

const LCBtnRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  margin-left: -8px;
`;

// CPAYMENT-1730: у варианта «уже оплатил» три исхода — в строку они не помещаются.
const LCBtnColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  margin-top: 24px;
`;

const lcBtnBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
  appearance: none;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  min-width: 64px;
  text-transform: none;
  outline: 0;
  border: 0;
  margin: 0;
  text-decoration: none;
  padding: 6px 8px;
  border-radius: 4px;
  transition: background-color 250ms cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1),
              border-color 250ms cubic-bezier(0.4, 0, 0.2, 1),
              color 250ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const LCBtnLeave = styled.button`
  ${lcBtnBase}
  color: ${PINCO_RED};
  &:hover { background-color: rgba(255, 1, 35, 0.08); }
`;

const LCBtnCancel = styled.button`
  ${lcBtnBase}
  color: ${PINCO_TEXT};
  &:hover { background-color: ${PINCO_CARD_BG_SOFT}; }
`;

// The cancel survey: optional, quieter than the buttons, so the decision still reads first.
const PincoReasonHint = styled.p`
  margin: 16px 0 8px;
  padding-top: 12px;
  border-top: 1px solid ${PINCO_BORDER};
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: ${PINCO_TEXT_MUTED};
  text-align: center;
`;

const PincoReasonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;

const PincoReasonChip = styled.button<{ $picked: boolean }>`
  padding: 6px 11px;
  border-radius: 999px;
  border: 1px solid ${({ $picked }) => ($picked ? PINCO_RED : PINCO_BORDER)};
  background: ${({ $picked }) => ($picked ? PINCO_CARD_BG_SOFT : 'transparent')};
  color: ${PINCO_TEXT};
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 12px;
  cursor: pointer;
`;

const PincoCancelSurvey = ({
  reasons,
  picked,
  onPick,
}: {
  reasons?: CancelReasonOption[];
  picked: CancelReasonOption | null;
  onPick: (reason: CancelReasonOption | null) => void;
}) => {
  const { t } = useTranslation();
  if (!reasons || reasons.length === 0) return null;

  return (
    <>
      <PincoReasonHint>{t('cancelReason.hint')}</PincoReasonHint>
      <PincoReasonRow>
        {reasons.map((reason) => (
          <PincoReasonChip
            key={reason.id}
            type='button'
            $picked={picked?.id === reason.id}
            data-sentry-element='onepage-cancel-reason'
            onClick={() => onPick(picked?.id === reason.id ? null : reason)}
          >
            {t(cancelReasonKey(reason.code))}
          </PincoReasonChip>
        ))}
      </PincoReasonRow>
    </>
  );
};

/** CPAYMENT-1730: см. `LionLeaveModal` — та же развилка выхода после «Я оплатил». */
export const PincoLeaveModal = ({
  onLeave,
  onCancel,
  receiptIntent,
  onLeaveWithoutCancel,
  reasons,
}: {
  onLeave: (reason?: CancelReasonOption) => void;
  onCancel: () => void;
  receiptIntent?: boolean;
  onLeaveWithoutCancel?: () => void;
  reasons?: CancelReasonOption[];
}) => {
  const { t } = useTranslation();
  // The pick stays in the dialog until the client actually cancels; a second tap clears it.
  const [picked, setPicked] = useState<CancelReasonOption | null>(null);
  const leave = () => onLeave(picked ?? undefined);

  if (receiptIntent && onLeaveWithoutCancel) {
    return (
      <LCOverlay onClick={onCancel}>
        <LCBox onClick={e => e.stopPropagation()}>
          <LCText>
            {t('modal.leavePaidText', 'Вы отметили, что оплатили. Если перевод уже сделан — не отменяйте заявку: дождитесь проверки платежа или обратитесь в поддержку.\nЕсли вы передумали и деньги не переводили — отмените заявку.')}
          </LCText>
          <PincoCancelSurvey reasons={reasons} picked={picked} onPick={setPicked} />
          <LCBtnColumn>
            <LCBtnCancel onClick={onLeaveWithoutCancel}>{t('modal.leaveToMerchant', 'Вернуться в сервис')}</LCBtnCancel>
            <LCBtnCancel onClick={onCancel}>{t('modal.stay', 'Остаться на странице')}</LCBtnCancel>
            <LCBtnLeave onClick={leave}>{t('modal.cancelTicket', 'Отменить заявку')}</LCBtnLeave>
          </LCBtnColumn>
        </LCBox>
      </LCOverlay>
    );
  }

  return (
    <LCOverlay onClick={onCancel}>
      <LCBox onClick={e => e.stopPropagation()}>
        <LCText>
          {t('modal.leaveText', "Пожалуйста дождитесь завершения оплаты.\nЕсли оплатили - нажмите кнопку 'Я оплатил'.\nЕсли уверены, что хотите покинуть страницу нажмите \"Покинуть страницу\"")}
        </LCText>
          <PincoCancelSurvey reasons={reasons} picked={picked} onPick={setPicked} />
        <LCBtnRow>
          <LCBtnLeave onClick={leave}>{t('modal.leave', 'Покинуть страницу')}</LCBtnLeave>
          <LCBtnCancel onClick={onCancel}>{t('modal.cancel', 'Отмена')}</LCBtnCancel>
        </LCBtnRow>
      </LCBox>
    </LCOverlay>
  );
};

const LCTitle = styled.p`
  margin: 0;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 1.4;
  text-align: left;
  color: ${PINCO_TEXT};
`;

const LCBtnPrimary = styled.button`
  ${lcBtnBase}
  color: ${PINCO_TEXT};
  background-color: ${PINCO_RED};
  padding: 10px 16px;
  &:hover { opacity: 0.9; }
`;

/** CPAYMENT-1731: см. `LionLowTimeModal` — тот же попап «мало времени», тексты через i18n. */
export const PincoLowTimeModal = ({
  onExtend,
  onDismiss,
}: {
  onExtend: () => void;
  onDismiss: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <LCOverlay onClick={onDismiss}>
      <LCBox onClick={e => e.stopPropagation()}>
        <LCTitle>{t('lowTime.title', 'Времени на оплату почти не осталось')}</LCTitle>
        <LCText>
          {t('lowTime.text', 'Если вы уже перевели деньги или переводите их сейчас — нажмите «Мне нужно ещё время», и мы продлим оплату, а затем попросим загрузить чек.')}
        </LCText>
        <LCBtnColumn>
          <LCBtnPrimary onClick={onExtend}>{t('lowTime.action', 'Мне нужно ещё время')}</LCBtnPrimary>
          <LCBtnCancel onClick={onDismiss}>{t('lowTime.dismiss', 'Закрыть')}</LCBtnCancel>
        </LCBtnColumn>
      </LCBox>
    </LCOverlay>
  );
};

export const PincoScreen = ({ step, withReceipt, uuid, timeLeft, actions, footerSlot, children }: PincoScreenProps) => {
  const hasConfirmLeave = actions?.some(a => a.confirmLeave) ?? false;

  useEffect(() => {
    if (!hasConfirmLeave) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      (e as any).returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasConfirmLeave]);

  return (
    <PincoRoot>
      <PincoCard>
        <PincoHeader />
        {step && <PincoSteps step={step} withReceipt={withReceipt} />}
        {children}
        {footerSlot}
        {actions?.map((a, i) => (
          <PincoButtonWrap key={i}>
            {a.variant === 'green'
              ? <PincoButtonGreen onClick={a.onClick}>{a.label}</PincoButtonGreen>
              : <PincoButtonGrey onClick={a.onClick}>{a.label}</PincoButtonGrey>
            }
          </PincoButtonWrap>
        ))}
        {timeLeft
          ? <PincoBottom timeLeft={timeLeft} uuid={uuid} />
          : uuid && <PincoOrderNum uuid={uuid} />
        }
      </PincoCard>
    </PincoRoot>
  );
};
